import type { ViewerCamera } from '@/lib/comrades/viewerPayload'
import type { EventPhase } from '@/lib/watch/rules'
import type { WatchConnectionState } from '@/lib/watch/types'
import type { GlyphMotion } from './StateGlyph'

/**
 * The event's sources as the empty states count them, offline ones included (even where `live_only` hides them from
 * the strip): its cameras (`source_role: 'camera'`) and its Live shows (`programme`), counted apart so a show alone
 * isn't called a camera.
 */
export interface CameraCount {
  cameras: number
  shows: number
}

/** Counts the payload's sources (those with a LiveKit identity) for the empty states. */
export function cameraCountOf(cameras: ViewerCamera[]): CameraCount {
  const sources = cameras.filter((camera) => camera.participant_identity)
  const shows = sources.filter((camera) => camera.source_role === 'programme').length
  return { cameras: sources.length - shows, shows }
}

/**
 * What the watch screen says when the room is connected and no camera can be watched, in the canvas and the phone's
 * Cameras tab (docs/plans/2026-10-17-watch-screen-web-notes.md, "Playful empty states"):
 *
 * - `camera`: the event has no cameras at all. The camera on the stacked disc, still, and "No cameras here": no "yet",
 *   as nothing new appears without a reload.
 * - `warm-up`: it has cameras (or a Live show) and none is live. The brand mark warming up: "Warming up" before the
 *   event's start (or with no start known), "Quiet stretch" once it has started. LiveKit brings a camera in as soon as
 *   it publishes, so "yet" and "right now" are honest in the plain words.
 * - `settle`: the same once the event's scheduled end has passed. The mark settles once and "All finished"; the plain
 *   words say only what is known, that the scheduled end has passed.
 *
 * `title` is what shows; `plain` (when the title is figurative) is the plain meaning, said first to screen readers.
 * Nothing here calls a camera live, connecting or offline.
 */
export interface QuietCanvas {
  art: 'camera' | 'warm-up' | 'settle'
  title: string
  plain?: string
}

export function quietCanvas({ cameras, shows }: CameraCount, phase: EventPhase): QuietCanvas {
  if (cameras === 0 && shows === 0) return { art: 'camera', title: 'No cameras here' }
  if (phase === 'after') return { art: 'settle', title: 'All finished', plain: 'The event’s scheduled end has passed' }
  const showOnly = cameras === 0
  if (phase === 'during') {
    return { art: 'warm-up', title: 'Quiet stretch', plain: showOnly ? 'The Live show isn’t live right now' : 'No camera is live right now' }
  }
  return { art: 'warm-up', title: 'Warming up', plain: showOnly ? 'The Live show isn’t live yet' : 'No camera is live yet' }
}

/** The quiet canvas's words where only screen readers get them (the swap card, the phone's grid): the plain meaning. */
export function quietWords(quiet: QuietCanvas): string {
  return quiet.plain ?? quiet.title
}

/**
 * The viewer's own connection while it tries, with the signal's motion: tuning in (connecting), tuning back in (LiveKit
 * reconnecting, or a new token replacing this one), and tuning back in too while a lost room fetches a new token on a
 * backoff, with the lost motion (the face lifting and settling, unlike the arcs filling in). The page's notice carries
 * the failure; the canvas never says the signal is lost, which would read as the race camera's. Each is an active
 * retry, so each moves.
 */
export function connectionWait(connection: Exclude<WatchConnectionState, 'connected'>): { words: string; motion: GlyphMotion } {
  if (connection === 'lost') return { words: 'Tuning back in…', motion: 'lost' }
  return { words: connection === 'reconnecting' ? 'Tuning back in…' : 'Tuning in…', motion: 'tune' }
}
